import { API_BASE_URL } from '@/config/env'
import { getAuthToken, setAuthToken, clearAuthToken } from './authSession'

/**
 * Centralized HTTP transport for backend API calls. Every request builds its
 * URL from `API_BASE_URL` (see src/config/env.ts) — no component or composable
 * should call `fetch` against the backend directly.
 *
 * Responsibility is limited to transport: building the request, parsing the
 * body, and normalizing failures into a typed `ApiError`. Mapping errors to
 * localized messages and reactive state belongs to the calling composable.
 *
 * Authenticated requests (`{ auth: true }`) attach the session bearer token and
 * transparently handle the backend's two-horizon JWT:
 *   - `422 { errors: 'REFRESH_TOKEN' }` → the access window expired but the
 *     token is still refreshable: we refresh once (single-flight) and replay.
 *   - terminal `401` (or a failed refresh) → the session is over: we clear the
 *     token and notify the app via the unauthorized handler.
 * Anonymous requests (the default) keep the exact behavior of the original
 * transport, so existing callers are unaffected.
 */

/**
 * Thrown for any non-2xx response or transport/parse failure.
 * `status` is the HTTP status, or 0 for network/abort/parse errors, so callers
 * can branch on it when choosing a localized message.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/* ------------------------------------------------------------------
 * Session-expired hook. The transport stays framework-agnostic: rather than
 * importing the router/locale here, the app registers a handler (Lot 1) that
 * performs the localized redirect to the login page. Until wired, it's a no-op.
 * ------------------------------------------------------------------*/
let onUnauthorized: (() => void) | null = null

/** Register the callback invoked when an authenticated session becomes invalid
 *  (terminal 401 or failed refresh). The token has already been cleared. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler
}

function handleUnauthorized(): void {
  clearAuthToken()
  onUnauthorized?.()
}

type QueryValue = string | number | boolean | undefined | null

/**
 * Build a full API URL from a resource path and optional query params.
 * `null`/`undefined` params are skipped so optional filters can be omitted.
 */
function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const base = `${API_BASE_URL}${path}`
  if (!query) return base
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) search.append(key, String(value))
  }
  const qs = search.toString()
  return qs ? `${base}?${qs}` : base
}

/** Best-effort parse of a response body — JSON when possible, otherwise undefined. */
async function safeParseBody(res: Response): Promise<unknown> {
  try {
    return await res.json()
  } catch {
    return undefined
  }
}

/** True when a response is the backend's "refresh your access token" signal
 *  (`422 { errors: 'REFRESH_TOKEN' }`) — distinct from a 422 validation error. */
function isRefreshTokenSignal(status: number, body: unknown): boolean {
  return (
    status === 422 &&
    typeof body === 'object' &&
    body !== null &&
    (body as { errors?: unknown }).errors === 'REFRESH_TOKEN'
  )
}

/* ------------------------------------------------------------------
 * Single-flight token refresh: concurrent authenticated requests that all hit
 * the refresh signal share one refresh call rather than stampeding the endpoint.
 * ------------------------------------------------------------------*/
let refreshPromise: Promise<string> | null = null

function refreshAuthToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

/** Exchange the current (refreshable) token for a fresh one via
 *  `PUT /sessions/refresh`. Persists and returns the new token, or throws. */
async function performRefresh(): Promise<string> {
  const token = getAuthToken()
  let res: Response
  try {
    res = await fetch(`${API_BASE_URL}/sessions/refresh`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
  } catch (cause) {
    throw new ApiError('Network request failed', 0, cause)
  }
  if (!res.ok) {
    throw new ApiError(`Token refresh failed with status ${res.status}`, res.status, await safeParseBody(res))
  }
  const data = (await res.json()) as { auth_token: string }
  setAuthToken(data.auth_token)
  return data.auth_token
}

type RequestInternal = {
  query?: Record<string, QueryValue>
  body?: unknown
  signal?: AbortSignal
  auth?: boolean
}

/**
 * Core request: performs the fetch, then for authenticated requests handles the
 * refresh signal (refresh once + replay) and terminal 401 (clear + notify).
 * Returns the raw, ok `Response` so callers parse it as JSON or as a Blob.
 */
async function execute(method: string, path: string, options: RequestInternal, isRetry = false): Promise<Response> {
  const { query, body, signal, auth } = options
  const token = auth ? getAuthToken() : null

  // A `FormData` body (file upload) is sent as-is: the browser sets
  // `Content-Type: multipart/form-data; boundary=…` itself.
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`

  let res: Response
  try {
    res = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body !== undefined ? (isForm ? (body as FormData) : JSON.stringify(body)) : undefined,
      signal,
    })
  } catch (cause) {
    // Network failure, DNS error, CORS rejection, or aborted request.
    throw new ApiError('Network request failed', 0, cause)
  }

  if (res.ok) return res

  const errorBody = await safeParseBody(res)

  // Access window expired but token still refreshable: refresh once, then replay.
  if (auth && !isRetry && isRefreshTokenSignal(res.status, errorBody)) {
    try {
      await refreshAuthToken()
    } catch {
      handleUnauthorized()
      throw new ApiError('Session expired', 401, errorBody)
    }
    return execute(method, path, options, true)
  }

  // Terminal unauthorized on an authenticated request: the session is over.
  if (auth && res.status === 401) handleUnauthorized()

  throw new ApiError(`Request failed with status ${res.status}`, res.status, errorBody)
}

export type GetOptions = {
  query?: Record<string, QueryValue>
  signal?: AbortSignal
  /** Attach the session bearer token and enable refresh/replay. Default: false. */
  auth?: boolean
}

/**
 * Perform a GET request and return the parsed JSON body.
 * Throws `ApiError` on non-2xx responses or transport/parse failures.
 */
export async function apiGet<T>(path: string, options: GetOptions = {}): Promise<T> {
  const res = await execute('GET', path, options)
  return (await res.json()) as T
}

/**
 * Perform a GET request and return the raw response body as a `Blob`
 * (binary downloads: contract certificate PDF, e-card PNG/PDF).
 * Throws `ApiError` on non-2xx responses or transport failures.
 */
export async function apiGetBlob(path: string, options: GetOptions = {}): Promise<Blob> {
  const res = await execute('GET', path, options)
  return await res.blob()
}

export type BodyOptions = {
  signal?: AbortSignal
  /** Attach the session bearer token and enable refresh/replay. Default: false. */
  auth?: boolean
}

/**
 * Perform a POST request with a JSON body and return the parsed JSON body.
 * Throws `ApiError` on non-2xx responses or transport/parse failures; the parsed
 * error body (e.g. a 422 validation payload) is carried on `ApiError.body`.
 */
export async function apiPost<T>(path: string, body: unknown, options: BodyOptions = {}): Promise<T> {
  const res = await execute('POST', path, { body, ...options })
  return (await res.json()) as T
}

/**
 * Perform a multipart POST (file upload) with a `FormData` body and return the
 * parsed JSON. The browser sets the `Content-Type` (with boundary); refresh/replay
 * still applies for authenticated requests. Throws `ApiError` on non-2xx.
 */
export async function apiPostForm<T>(path: string, form: FormData, options: BodyOptions = {}): Promise<T> {
  const res = await execute('POST', path, { body: form, ...options })
  return (await res.json()) as T
}

/**
 * Perform a PUT request with an optional JSON body and return the parsed JSON.
 * Throws `ApiError` on non-2xx responses or transport/parse failures.
 */
export async function apiPut<T>(path: string, body?: unknown, options: BodyOptions = {}): Promise<T> {
  const res = await execute('PUT', path, { body, ...options })
  return (await res.json()) as T
}

/**
 * Perform a PATCH request with a JSON body and return the parsed JSON body.
 * Throws `ApiError` on non-2xx responses or transport/parse failures; the parsed
 * error body (e.g. a 422 validation payload) is carried on `ApiError.body`.
 */
export async function apiPatch<T>(path: string, body: unknown, options: BodyOptions = {}): Promise<T> {
  const res = await execute('PATCH', path, { body, ...options })
  return (await res.json()) as T
}
